import { useEffect, useRef, useState } from 'preact/hooks';
import { lectureCameraDisponible, lireCode, ouvrirCamera } from '../lecteur/camera';

export interface ProprietesScan {
  readonly enCours: boolean;
  readonly onContenu: (contenu: string) => void;
  readonly onAbandon: () => void;
}

/** Cadence d'analyse : quatre images par seconde suffisent et ménagent la batterie. */
const INTERVALLE_ANALYSE_MS = 250;

/**
 * Scan du QR Code, avec **saisie manuelle toujours offerte**.
 *
 * Le repli n'est pas un pis-aller réservé aux téléphones anciens : sur un pont,
 * une étiquette déchirée ou effacée est courante. Un agent doit pouvoir recopier
 * ce qu'il lit, sans quoi le contrôle s'arrête faute d'image nette.
 */
export function EcranScan({ enCours, onContenu, onAbandon }: ProprietesScan) {
  const video = useRef<HTMLVideoElement | null>(null);
  const [cameraActive, setCameraActive] = useState(false);
  const [messageCamera, setMessageCamera] = useState<string | null>(null);
  const [saisie, setSaisie] = useState('');

  useEffect(() => {
    if (!lectureCameraDisponible()) {
      setMessageCamera(
        'Ce téléphone ne sait pas lire les QR Codes. Saisissez le contenu à la main.',
      );
      return;
    }

    let arreter: (() => void) | null = null;
    // Le type de la minuterie diffère entre le navigateur et Node : le déduire
    // évite d'imposer l'un des deux.
    let minuterie: ReturnType<typeof globalThis.setTimeout> | undefined;

    // Porté par un objet, et non par une variable locale : le démontage peut
    // survenir pendant une attente, et l'analyse de flux ne le voit pas.
    // Sans ces gardes, on écrirait dans un composant déjà retiré et la caméra
    // continuerait de tourner.
    const monte = { valeur: true };
    // Lu par appel : le résultat d'une fonction n'est pas rétréci par l'analyse de
    // flux, qui sinon tiendrait ces gardes pour acquises et les signalerait
    // inutiles — alors qu'elles protègent d'un rappel après démontage.
    const estMonte = (): boolean => monte.valeur;

    void (async () => {
      try {
        const session = await ouvrirCamera();
        if (!estMonte()) {
          session.arreter();
          return;
        }
        arreter = session.arreter;
        if (video.current !== null) {
          video.current.srcObject = session.flux;
          await video.current.play();
        }
        setCameraActive(true);

        const analyser = async (): Promise<void> => {
          if (!estMonte() || video.current === null) return;
          const contenu = await lireCode(video.current);
          if (!estMonte()) return;
          if (contenu !== null) {
            onContenu(contenu);
            return;
          }
          minuterie = globalThis.setTimeout(() => {
            void analyser();
          }, INTERVALLE_ANALYSE_MS);
        };
        void analyser();
      } catch {
        // Autorisation refusée, ou aucune caméra : la saisie manuelle prend le
        // relais sans que l'application perde sa fonction.
        if (estMonte()) {
          setMessageCamera('Caméra indisponible. Saisissez le contenu du QR Code à la main.');
        }
      }
    })();

    return () => {
      monte.valeur = false;
      if (minuterie !== undefined) globalThis.clearTimeout(minuterie);
      // Sans cet arrêt, la diode de la caméra resterait allumée après le contrôle.
      arreter?.();
    };
  }, [onContenu]);

  // Le mot doit dire l'état réel de l'appareil. Annoncer « Scan » en grand sur
  // un téléphone qui ne sait pas lire de QR Code désigne une action impossible,
  // et le contrôleur cherche une visée qui ne viendra pas.
  const saisieSeule = !cameraActive && messageCamera !== null;

  return (
    <div className="verify-ecran" data-verdict="attente">
      <div className="verify-verdict">
        <h1 className="verify-verdict__mot">{saisieSeule ? 'Saisie' : 'Scan'}</h1>
        {cameraActive ? (
          <video
            ref={video}
            className="verify-camera"
            playsInline
            muted
            aria-label="Visée de la caméra"
          />
        ) : (
          <p className="verify-verdict__explication">
            {messageCamera ?? 'Ouverture de la caméra…'}
          </p>
        )}
        {enCours && <p className="verify-verdict__explication">Vérification…</p>}
      </div>

      <div className="verify-saisie">
        {/* « Ou » suppose une première branche : sans caméra, il n'y en a pas. */}
        <label htmlFor="saisie-manuelle">
          {saisieSeule ? 'Contenu du QR Code' : 'Ou saisissez le contenu du QR Code'}
        </label>
        <textarea
          id="saisie-manuelle"
          rows={3}
          value={saisie}
          onInput={(evenement) => {
            setSaisie(evenement.currentTarget.value);
          }}
        />
      </div>

      <div className="verify-actions">
        <button
          type="button"
          className="verify-bouton verify-bouton--principal"
          disabled={saisie.trim() === '' || enCours}
          onClick={() => {
            onContenu(saisie.trim());
          }}
        >
          Vérifier ce contenu
        </button>
        <button type="button" className="verify-bouton" onClick={onAbandon}>
          Annuler
        </button>
      </div>
    </div>
  );
}
